import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import "./styles/index.less";

import ProtectedRoute from "./shared/protected-route/protectedRoute";
import LandingPage from "./components/Landing Page/landingPage";
import HomePage from "./components/Home Page/homePage";
import Navbar from "./shared/Navbar/navbar";
import Faq from "./shared/FAQ/faq";
import About from "./shared/About Us/about";
import Contact from "./shared/Contact Us/contact";
import Footer from "./shared/Footer/footer";
import ReitsListing from "./components/Listing Page/REITs/reitsListing";
import InvitsListing from "./components/Listing Page/INVITs/invitsListing";
import { AuthProvider } from "./context/authContext";
import { FlyoutProvider } from "./context/layoutContext";
import ResetPassword from "./components/reset-password/resetPassword";
import ReitsDetail from "./components/Detail Page/REITs/reitsDetail";
import InvitsDetail from "./components/Detail Page/INVITs/InvitDetail";
import ReitsValuation from "./components/Valuation Page/REITs/reitsValuation";
import InvITsValuation from "./components/Valuation Page/InvITs/invitsValuation";
import ReitsCompare from "./components/Compare-Page/Reits/reitsCompare";
import CompareReits from "./components/Compare-Reits/compareReits";
import InvITsFinancial from "./components/InvITs Financial/financial";
import InvITsOperational from "./components/InvITs Operational/operational";
import { ParametersProvider } from "./context/parametersContext";
import { ToastContainer } from "react-toastify";
import ScrollToTop from "./shared/Scroll-To-Top/ScrollToTop";

// Main App component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <FlyoutProvider>
        <ParametersProvider>
          <Router>
            <div className="app">
              <Navbar />
              <AppRoutes />
              <ScrollToTop />
              <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick={true}
                rtl={false}
                pauseOnFocusLoss={true}
                draggable={true}
                pauseOnHover={true}
                theme="light"
                className="custom-toast-container"
                toastClassName="custom-toast"
                progressClassName="custom-progress-bar"
              />
            </div>
          </Router>
        </ParametersProvider>
      </FlyoutProvider>
    </AuthProvider>
  );
};

// All routes separated for cleaner navigation
const AppRoutes: React.FC = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<"REITs" | "InvITs">("REITs");

  return (
    <Routes>
      {/* Landing Page */}
      <Route
        path="/"
        element={<LandingPage onGetStarted={() => navigate("/home")} />}
      />

      {/* Home route */}
      <Route path="/home" element={<HomePage />} />

      {/* REITs Listing */}
      <Route
        path="/reits"
        element={
          <>
            <ReitsListing />
            <Faq activeTab={activeTab} setActiveTab={setActiveTab} />
            <Footer />
          </>
        }
      />

      {/* INVITs Listing */}
      <Route
        path="/invits"
        element={
          <>
            <InvitsListing />
            <Faq activeTab={activeTab} setActiveTab={setActiveTab} />
            <Footer />
          </>
        }
      />

      {/* Reset Password */}
      <Route
        path="/reset-password/:token"
        element={
          <ResetPassword
            isOpen={true}
            closeModal={() => {}}
            onBackToLogin={() => {}}
          />
        }
      />

      {/* About Us */}
      <Route
        path="/about"
        element={
          <>
            <About />
            <Footer />
          </>
        }
      />

      {/* Contact Us */}
      <Route
        path="/contact"
        element={
          <>
            <Contact />
            <Footer />
          </>
        }
      />

      {/* REITs Detail */}
      <Route
        path="/reits/:id"
        element={
          <ProtectedRoute>
            <ReitsDetail />
            <Footer />
          </ProtectedRoute>
        }
      />

      {/* InvITs Detail */}
      <Route
        path="/invits/:id"
        element={
          <ProtectedRoute>
            <InvitsDetail />
            <Footer />
          </ProtectedRoute>
        }
      />

      {/* REITs Valuation */}
      <Route
        path="/reits/:id/valuation"
        element={
          <ProtectedRoute>
            <ReitsValuation />
            <Footer />
          </ProtectedRoute>
        }
      />

      {/* InvITs Valuation */}
      <Route
        path="/invits/:id/valuation"
        element={
          <ProtectedRoute>
            <InvITsValuation />
            <Footer />
          </ProtectedRoute>
        }
      />

      {/* REITs Compare */}
      <Route
        path="/reits/compare-properties"
        element={
          <ProtectedRoute>
            <ReitsCompare />
            <Footer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reits/compare"
        element={
          <ProtectedRoute>
            <CompareReits />
            <Footer />
          </ProtectedRoute>
        }
      />

      {/* InvITs Financial & Operational Compare */}
      <Route
        path="/invits/financial-compare"
        element={
          <ProtectedRoute>
            <InvITsFinancial />
            <Footer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invits/operational-compare"
        element={
          <ProtectedRoute>
            <InvITsOperational />
            <Footer />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default App;
